import { tt } from './i18n'
import { formatFixed } from './percent'

/**
 * What the AI translations have cost - FRONTEND.md §9.255, FB-415.
 *
 * `GET /ai/translation-usage` is served, answers 200, and was read by nothing - while the page it
 * belongs on carries **two buttons that each start hundreds of billable calls** on the owner's Claude
 * key (`AiDtos`: *"the two bulk endpoints start a run over the whole menu - hundreds of calls and
 * several minutes"*).
 *
 * **In `shared` rather than in the manager module - `SAAS.md` §3.89.** The Control Tower now reads
 * the same report through `GET /super-admin/franchises/{id}/translation-usage`, and a Tower app
 * importing a manager module would be a layering break while a second copy of this function would be
 * one payload with two readers - which is the shape of most of this repository's defects
 * (`agos-one-reader-spelled-twice`). One reader, two callers, and `manager-settings` re-exports it
 * under its old path so nothing that used it had to move.
 *
 * **The payload mixes cases in one body and that is deliberate on the server.** `totals.inputTokens`
 * is camelCase because the totals are assembled in Java; `byLanguage[].input_tokens` is snake_case
 * because those rows come straight off a `GROUP BY`. `AiDtos` says renaming either breaks whichever
 * screen reads it, so this reads both rather than picking one.
 */

export interface UsageTotals {
  totalCalls: number
  okCalls: number
  errorCalls: number
  inputTokens: number
  outputTokens: number
  /**
   * Euros at six decimal places, as sent.
   *
   * `AiDtos`: *"0.158823 for the month measured… Rounding it to two would report every call as
   * free."* So it is kept as a number here and formatted where it is printed, never through
   * `formatCents`.
   */
  costEur: number
  /** Postgres text - `2026-07-28 18:30:56.500302+00` - not ISO. */
  lastCallAt: string | null
}

export interface UsageLanguage {
  lang: string
  calls: number
  inputTokens: number
  outputTokens: number
  costEur: number
}

export interface UsageError {
  status: string
  errorMessage: string | null
  targetLang: string | null
  createdAt: string | null
  sourceExcerpt: string | null
}

export interface TranslationUsage {
  totals: UsageTotals
  byLanguage: UsageLanguage[]
  recentErrors: UsageError[]
}

function num(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null
}

export function translationUsageOf(payload: unknown): TranslationUsage {
  const row = (typeof payload === 'object' && payload !== null ? payload : {}) as Record<string, unknown>
  const totals = (typeof row.totals === 'object' && row.totals !== null ? row.totals : {}) as Record<
    string,
    unknown
  >
  const languages = Array.isArray(row.byLanguage) ? (row.byLanguage as Record<string, unknown>[]) : []
  const errors = Array.isArray(row.recentErrors) ? (row.recentErrors as Record<string, unknown>[]) : []

  return {
    totals: {
      totalCalls: num(totals.totalCalls),
      okCalls: num(totals.okCalls),
      errorCalls: num(totals.errorCalls),
      inputTokens: num(totals.inputTokens),
      outputTokens: num(totals.outputTokens),
      costEur: num(totals.costEur),
      lastCallAt: text(totals.lastCallAt),
    },
    byLanguage: languages.flatMap((entry) => {
      const lang = text(entry.lang)
      if (lang === null) return []
      return [
        {
          lang,
          calls: num(entry.calls),
          // snake_case here, camelCase in `totals` - the server's own split, read as it arrives.
          inputTokens: num(entry.input_tokens),
          outputTokens: num(entry.output_tokens),
          costEur: num(entry.cost_eur),
        },
      ]
    }),
    recentErrors: errors.map((entry) => ({
      status: text(entry.status) ?? '',
      errorMessage: text(entry.errorMessage),
      targetLang: text(entry.targetLang),
      createdAt: text(entry.createdAt),
      sourceExcerpt: text(entry.sourceExcerpt),
    })),
  }
}

/**
 * `0,158823 €` - six decimals, because two would report every call as free.
 *
 * Not `formatCents`: this is not a price and rounding it to a cent is the specific mistake `AiDtos`
 * warns about. A cost of exactly nought prints as `0 €` rather than `0,000000 €`, because six zeros
 * read as a broken figure.
 */
export function formatCost(costEur: number): string {
  if (costEur <= 0) return '0 €'
  // `formatFixed`, not `toFixed`: N-02 exists because a hand-rolled `toFixed(…).replace('.', ',')`
  // is right about the comma and silent about the thousands separator. Fixed width rather than
  // `formatDecimal`'s maximum, because trailing zeroes here are the stated precision.
  return `${formatFixed(costEur, 6)} €`
}

/** Whether there is anything to report at all. */
export function hasUsage(usage: TranslationUsage): boolean {
  return usage.totals.totalCalls > 0
}

/**
 * The period the usage report is asked about - `SAAS.md §3.98`.
 *
 * **A named range, not `ReportWindow`.** §3.84's bar produces `from` and `to` ISO instants because
 * the four reports it serves declare those parameters; this endpoint declares `range` and nothing
 * else, so its vocabulary is the server's seven names. The two are deliberately separate: a
 * `from=`/`to=` pair sent here would be ignored and the operator would see the default window under
 * a pressed button.
 *
 * Read off `TranslationUsage.windowFor`, which is also where the meanings live: `this_month` and
 * `last_month` are calendar months and the rest are rolling durations back from now.
 */
export type UsageRange = 'day' | 'week' | 'month' | 'this_month' | 'last_month' | 'year' | 'all'

/**
 * Legacy's seven, in legacy's order.
 *
 * **`all` is last and is the default, which is the one place this differs from legacy.** §3.89 chose
 * it deliberately: a support question about a rejected key is *has this ever worked*, and a thirty-day
 * default answers *nothing logged* for a franchise with thousands of rejections from before the
 * restore. Legacy defaults to `month`.
 */
export const USAGE_RANGES: readonly UsageRange[] = [
  'day',
  'week',
  'month',
  'this_month',
  'last_month',
  'year',
  'all',
]

/** What the panel opens on, and the range whose emptiness means *nothing to report at all*. */
export const DEFAULT_USAGE_RANGE: UsageRange = 'all'

/** Legacy's own labels, because they are the vocabulary the owner reads these screens in. */
export function usageRangeLabel(range: UsageRange): string {
  if (range === 'day') return tt('Deň')
  if (range === 'week') return tt('Týždeň')
  // `month` is the server's rolling 30 days, not a calendar month - `this_month` is that. Labelling
  // it *Mesiac* beside *Tento mesiac* would make two buttons look like the same question.
  if (range === 'month') return tt('30 dní')
  if (range === 'this_month') return tt('Tento mesiac')
  if (range === 'last_month') return tt('Minulý mesiac')
  if (range === 'year') return tt('Rok')
  return tt('Celkovo')
}

/** *Za toto obdobie sa nič neprekladalo* - said when the chosen range is empty and the panel stays. */
export function emptyRangeNote(range: UsageRange): string {
  // **Why this exists.** The panel is only rendered at all when the franchise has usage *somewhere*
  // (`hasUsage` at `range=all`), so an empty selected range is a real answer rather than an absence
  // of data - and it must be said, because otherwise a row of zeroes reads as a broken report.
  return `${tt('V tomto období sa neprekladalo nič')} (${usageRangeLabel(range)}).`
}

/** *Za posledný mesiac: 0 volaní* - said when nothing has run, rather than a panel of zeroes. */
export function emptyUsageNote(): string {
  return tt('Zatiaľ nebolo preložené nič - preklad sa spustí tlačidlami nižšie a účtuje sa za volanie.')
}
